import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { FaAngleLeft } from 'react-icons/fa6';
import { validateVirtualCardFunding, initiateVirtualCardFunding, getVirtualCardDetails, getVirtualCardProducts } from '../../../../api/api';
import { fetchExchangeRates } from '../../../../redux/slices/countriesSlice';
import CardOperationSummary from '../CardOperationSummary';

const FundCard = ({ userDetails }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cardId } = useParams();
  const [amount, setAmount] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [validatedPayload, setValidatedPayload] = useState(null);
  const [cardDetails, setCardDetails] = useState(null);
  const [products, setProducts] = useState([]);

  const { conversionRates } = useSelector((state) => state.country);

  useEffect(() => {
    const fetchCardAndProducts = async () => {
      try {
        const [cardResponse, productsResponse] = await Promise.all([
          getVirtualCardDetails(cardId),
          getVirtualCardProducts()
        ]);
        setCardDetails(cardResponse.data?.data);
        setProducts(productsResponse.data?.data?.virtual_card_products || []); 
      } catch (error) {
        console.error("Failed to load data", error);
      }
    };
    if (cardId) fetchCardAndProducts();
  }, [cardId]);

  useEffect(() => {
    dispatch(fetchExchangeRates());
  }, [dispatch]);

  const cardCurrency = cardDetails?.currency || cardDetails?.card?.currency || 'USD';
  const walletCurrency = userDetails?.default_wallet?.currency_code || 'NGN';

  const getExchangeRate = () => {
    if (cardCurrency === walletCurrency) return 1;
    const walletRateData = (conversionRates || []).find(r => r.currency_code === walletCurrency);
    return walletRateData ? parseFloat(walletRateData.buy_rate) : 0;
  };

  const usdToNgnRate = getExchangeRate();

 
  const calculateFundingFee = () => {
    if (!products.length || !amount) return 0;
    
    let matchingProduct = products[0];
    if (cardDetails) {
        matchingProduct = products.find(p => 
          p.currency?.toLowerCase() === (cardDetails.currency || cardDetails.card?.currency || 'usd').toLowerCase()
        ) || products[0];
    }
    
    if (!matchingProduct || !matchingProduct.fee_configurations) return 0;
    
    const fundConfigs = matchingProduct.fee_configurations.filter(f => f.card_action?.toLowerCase() === 'fund');
    if (!fundConfigs.length) return 0;
    
    const amt = parseFloat(amount);
    let applicableConfig = fundConfigs.find(f => {
      const min = f.minimum_amount ? parseFloat(f.minimum_amount) : 0;
      const max = f.maximum_amount ? parseFloat(f.maximum_amount) : Infinity;
      return amt >= min && amt <= max;
    });

    if (!applicableConfig) {
       applicableConfig = fundConfigs[0]; 
    }
    
    if (applicableConfig.fee_type === 'flat') {
      return parseFloat(applicableConfig.flat_fee_amount || 0);
    } else if (applicableConfig.fee_type === 'percentage') {
      return (amt * parseFloat(applicableConfig.fee_percentage || 0)) / 100;
    }else if (applicableConfig.fee_type === 'hybrid'){
      return parseFloat(applicableConfig.flat_fee_amount) + ((amt * parseFloat(applicableConfig.fee_percentage || 0)) / 100);
    }
    return 0;
  };

  const fundingFee = calculateFundingFee();
  const totalAmountUsd = (parseFloat(amount) || 0) + fundingFee;
  const totalAmount = totalAmountUsd * usdToNgnRate;
  const formatWalletCurrency = (value) => new Intl.NumberFormat(
    walletCurrency === "NGN" ? "en-NG" : "en-US", {
    style: "currency",
    currency: walletCurrency,
    minimumFractionDigits: 2,
  }).format(value);
  const formatCardCurrency = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: cardCurrency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
  }).format(value);
  const walletBalance = Number(
    String(userDetails?.default_wallet?.balance || 0).replace(/[^0-9.-]+/g, "")
  ) || 0;

  // Load Monnify SDK dynamically
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://sdk.monnify.com/plugin/monnify.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleFund = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setLoading(true);
    try {
      const payload = { funding_amount: parseFloat(amount) };
      const response = await validateVirtualCardFunding(cardId, payload);
      setValidationData(response.data?.data || {});
      setValidatedPayload(payload);
      
      const modal = new Modal(document.getElementById('fundModal'));
      modal.show();
    } catch (error) {
      const responseData = error.response?.data;
      const fieldError = responseData?.errors
        ? Object.values(responseData.errors).flat().find(Boolean)
        : null;
      toast.error(fieldError || responseData?.message || "Validation failed for funding", {
          toastId: "funding-fail-toast",
          className: "custom-toast",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmFunding = async () => {
    setInitiating(true);
    try {
      if (!validatedPayload) return;
      await initiateVirtualCardFunding(cardId, validatedPayload);
      
      const modalEl = document.getElementById('fundModal');
      const modal = Modal.getInstance(modalEl);
      if (modal) modal.hide();

      toast.success("Funding successful!", {
          toastId: "funding-success-toast",
          className: "custom-toast",
      });
      
      setAmount("");
      navigate(`/user/cards`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Funding failed!", {
          toastId: "funding-fail-toast",
          className: "custom-toast",
      });
    } finally {
      setInitiating(false);
    }
  };

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    setIsEdited(true);
  };

  return (
    <section className="create__card__page card__fund__page">
      <section className="create-card_section funding__card__section">
        <div className="container py-4 bank-fund-page">
          <div className="bank-fund-heading">
            <button
              onClick={() => navigate(-1)}
              className="bank-fund-back-icon"
              aria-label="Go back"
            >
              <FaAngleLeft size={15} />
            </button>
            <h2>Fund Card</h2>
          </div>

          <div className="bank-fund-content">


            <div className="bank-fund-form">
              {/* <div className="bank-fund-form-intro">
                <div className="bank-fund-form-icon"><FaBuildingColumns /></div>
                <div>
                  <h3>Fund Card</h3>
                  <p>Enter the amount you want to add to your virtual card.</p>
                </div>
              </div> */}
              <div className="bank-fund-field">
                <label htmlFor="fund-card-amount">Amount in USD</label>
                <div className="bank-fund-input-wrap">
                  <span>$</span>
                  <input
                    id="fund-card-amount"
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={handleAmountChange}
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div className="bank-fund-quick-amounts">
                  {[25, 50, 100, 250].map((quickAmount) => (
                    <button key={quickAmount} type="button" onClick={() => { setAmount(String(quickAmount)); setIsEdited(true); }}>
                      ${quickAmount}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bank-fund-summary">
                <div><span>Rate</span><strong>{usdToNgnRate ? `1 ${cardCurrency} = ${formatWalletCurrency(usdToNgnRate)}` : "Loading rate..."}</strong></div>
                <div><span>Funding Fee</span><strong>{`$${fundingFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}</strong></div>
                <div className="bank-fund-total"><span>Total Amount</span><strong>{formatWalletCurrency(totalAmount)}</strong></div>
              </div>

                <button
                  onClick={handleFund}
                  className="bank-fund-submit"
                  disabled={!isEdited || !amount || parseFloat(amount) <= 0 || loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : "Continue"}
                </button>
            </div>
          </div>
        </div>
      </section>

      {/* Bootstrap  */}
      <section className="modal fade" id="fundModal" tabIndex="-1" aria-labelledby="fundModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                  <div className="d-flex justify-content-end p-3 pb-0">
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div className="modal-body  mb-4 mx-4 text-center">
                      <CardOperationSummary
                        operationType="fund"
                        title=""
                        data={{
                          ...validationData,
                          card_event_amount: formatWalletCurrency(totalAmount),
                          wallet_impact: {
                            display_title: "Your Wallet",
                            display_subtitle: `Balance: ${formatWalletCurrency(walletBalance)}`,
                          },
                          funding_amount: formatCardCurrency(validatedPayload?.funding_amount || 0),
                        }}
                        primaryAmount={{
                          value: formatCardCurrency(validatedPayload?.funding_amount || 0),
                          label: "Funding amount",
                        }}
                        hiddenKeys={["currency", "balance", "available_balance"]}
                        hiddenGroupKeys={["account"]}
                      />

                      <div className="d-flex gap-3 mt-4">
                          <button className="btn btn-outline-secondary w-50 rounded-pill py-2.5 fw-semibold" data-bs-dismiss="modal" aria-label="Close" disabled={initiating} style={{ fontSize: '14px' }}>
                              Cancel
                          </button>
                          <button className="btn primary__btn w-50 rounded-pill py-2.5 fw-semibold" onClick={handleConfirmFunding} disabled={initiating} style={{ fontSize: '14px' }}>
                              {initiating ? "Processing..." : "Fund Card"}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      </section>
    </section>
  );
};

export default FundCard;
